<template>
  <div class="facebook-pixel-page">
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h2 class="page-title mb-50">Facebook Pixel Manager</h2>
          <p class="page-subtitle mb-0">
            Standalone Facebook Pixel + optional CAPI configuration. Only one active config works at a time.
          </p>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col xl="5" lg="5">
        <b-card class="pixel-card mb-2">
          <div class="mb-2">
            <h4 class="mb-25">{{ editMode ? 'Edit Pixel' : 'Add New Pixel' }}</h4>
            <small class="text-muted">Pixel ID required, Access Token and Test Event Code optional.</small>
          </div>

          <b-form @submit.prevent="submitForm">
            <b-form-group label="Name">
              <b-form-input
                v-model.trim="form.name"
                placeholder="Example: Main Pixel"
                required
              />
            </b-form-group>

            <b-form-group label="Pixel ID">
              <b-form-input
                v-model.trim="form.pixel_id"
                placeholder="Example: 123456789012345"
                required
              />
            </b-form-group>

            <b-form-group label="Access Token (Optional)">
              <b-form-textarea
                v-model.trim="form.access_token"
                placeholder="For Conversion API"
                rows="3"
              />
            </b-form-group>

            <b-form-group label="Test Event Code (Optional)">
              <b-form-input
                v-model.trim="form.test_event_code"
                placeholder="Example: TEST12345"
              />
            </b-form-group>

            <div class="d-flex flex-wrap gap-1">
              <b-button
                type="submit"
                variant="primary"
                :disabled="submitting"
              >
                {{ submitting ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update Pixel' : 'Save Pixel') }}
              </b-button>

              <b-button
                type="button"
                variant="outline-secondary"
                @click="resetForm"
                :disabled="submitting"
              >
                Reset
              </b-button>

              <b-button
                v-if="editMode"
                type="button"
                variant="outline-danger"
                @click="cancelEdit"
                :disabled="submitting"
              >
                Cancel Edit
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>

      <b-col xl="7" lg="7">
        <b-card class="pixel-card mb-2">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <div>
              <h4 class="mb-25">Saved Pixels</h4>
              <small class="text-muted">Multiple configs can be stored, but only one remains active.</small>
            </div>

            <div class="d-flex flex-wrap gap-1">
              <b-button
                variant="outline-warning"
                size="sm"
                @click="deactivateAll"
                :disabled="deactivatingAll || loading"
              >
                {{ deactivatingAll ? 'Processing...' : 'Deactivate All' }}
              </b-button>

              <b-button
                variant="outline-primary"
                size="sm"
                @click="loadPixels"
                :disabled="loading"
              >
                Refresh
              </b-button>
            </div>
          </div>

          <div v-if="loading" class="loading-wrap">
            Loading pixels...
          </div>

          <div v-else class="table-responsive">
            <table class="table pixel-table mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Pixel ID</th>
                  <th>CAPI</th>
                  <th>Test Code</th>
                  <th>Status</th>
                  <th class="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in pixels" :key="item.id">
                  <td>
                    <div class="font-weight-bold">{{ item.name }}</div>
                  </td>

                  <td>
                    <span class="mono">{{ item.pixel_id }}</span>
                  </td>

                  <td>
                    <b-badge :variant="item.access_token ? 'success' : 'light'">
                      {{ item.access_token ? 'Enabled' : 'Not Set' }}
                    </b-badge>
                  </td>

                  <td>
                    <span v-if="item.test_event_code" class="mono">
                      {{ item.test_event_code }}
                    </span>
                    <span v-else class="text-muted">�</span>
                  </td>

                  <td>
                    <b-badge pill :variant="item.is_active ? 'success' : 'secondary'">
                      {{ item.is_active ? 'Active' : 'Inactive' }}
                    </b-badge>
                  </td>

                  <td class="text-right">
                    <div class="d-flex justify-content-end flex-wrap gap-1">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="startEdit(item)"
                      >
                        Edit
                      </b-button>

                      <b-button
                        v-if="!item.is_active"
                        size="sm"
                        variant="outline-success"
                        @click="activatePixel(item.id)"
                        :disabled="activatingId === item.id"
                      >
                        {{ activatingId === item.id ? 'Activating...' : 'Activate' }}
                      </b-button>

                      <b-badge
                        v-else
                        variant="success"
                        class="d-flex align-items-center px-1"
                      >
                        Running
                      </b-badge>

                      <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="deletePixel(item)"
                        :disabled="deletingId === item.id"
                      >
                        {{ deletingId === item.id ? 'Deleting...' : 'Delete' }}
                      </b-button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!pixels.length">
                  <td colspan="6" class="text-center text-muted py-3">
                    No Facebook Pixel configuration found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <b-card class="pixel-card mb-2">
          <div class="mb-2">
            <h4 class="mb-25">How It Works</h4>
          </div>

          <ul class="info-list mb-0">
            <li>Only one pixel can stay active at a time.</li>
            <li>You can keep all records inactive if you do not want any standalone pixel running.</li>
            <li>Frontend tracker sends Pixel browser events automatically when a config is active.</li>
            <li>If Access Token is added, backend also sends CAPI events.</li>
            <li>Existing GTM remains untouched.</li>
          </ul>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from '@/helpers/axios'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'FacebookPixelManager',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BBadge,
  },

  data() {
    return {
      loading: false,
      submitting: false,
      activatingId: null,
      deletingId: null,
      deactivatingAll: false,
      editMode: false,
      editingId: null,
      pixels: [],
      form: {
        name: '',
        pixel_id: '',
        access_token: '',
        test_event_code: '',
      },
    }
  },

  async mounted() {
    await this.loadPixels()
  },

  methods: {
    async loadPixels() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/fb-pixels')
        this.pixels = Array.isArray(data) ? data : []
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Failed to load Facebook pixels', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.form = {
        name: '',
        pixel_id: '',
        access_token: '',
        test_event_code: '',
      }
    },

    cancelEdit() {
      this.editMode = false
      this.editingId = null
      this.resetForm()
    },

    startEdit(item) {
      this.editMode = true
      this.editingId = item.id
      this.form = {
        name: item.name || '',
        pixel_id: item.pixel_id || '',
        access_token: item.access_token || '',
        test_event_code: item.test_event_code || '',
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    async submitForm() {
      this.submitting = true
      try {
        if (this.editMode && this.editingId) {
          await axios.put(`/api/fb-pixels/${this.editingId}`, this.form)

          this.$bvToast?.toast('Facebook Pixel updated successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
        } else {
          await axios.post('/api/fb-pixels', this.form)

          this.$bvToast?.toast('Facebook Pixel saved successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
        }

        this.cancelEdit()
        await this.loadPixels()
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast(this.editMode ? 'Failed to update Facebook Pixel' : 'Failed to save Facebook Pixel', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.submitting = false
      }
    },

    async activatePixel(id) {
      this.activatingId = id
      try {
        await axios.post(`/api/fb-pixels/${id}/activate`)

        this.$bvToast?.toast('Facebook Pixel activated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })

        await this.loadPixels()
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Failed to activate Facebook Pixel', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.activatingId = null
      }
    },

    async deactivateAll() {
      this.deactivatingAll = true
      try {
        await axios.post('/api/fb-pixels/deactivate-all')

        this.$bvToast?.toast('All Facebook Pixels are now inactive', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })

        await this.loadPixels()
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Failed to deactivate pixels', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.deactivatingAll = false
      }
    },

    async deletePixel(item) {
      const ok = window.confirm(`Delete "${item.name}"?`)
      if (!ok) return

      this.deletingId = item.id
      try {
        await axios.delete(`/api/fb-pixels/${item.id}`)

        this.$bvToast?.toast('Facebook Pixel deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })

        if (this.editingId === item.id) {
          this.cancelEdit()
        }

        await this.loadPixels()
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Failed to delete Facebook Pixel', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style scoped>
.facebook-pixel-page {
  padding: 10px;
}

.page-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #5e5873;
}

.page-subtitle {
  color: #6e6b7b;
}

.pixel-card {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.08);
}

.pixel-table thead th {
  border-top: 0;
  color: #6e6b7b;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.pixel-table tbody td {
  vertical-align: middle;
}

.mono {
  font-family: monospace;
  font-size: 0.92rem;
}

.loading-wrap {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6e6b7b;
}

.info-list {
  padding-left: 18px;
  color: #6e6b7b;
}

.gap-1 {
  gap: 10px;
}
</style>